import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/Layout';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, Plus, ChevronRight, CheckCircle2, Clock, Send, Edit3,
  AlertCircle, X, MessageCircle, Download, Sparkles
} from 'lucide-react';
import confetti from 'canvas-confetti';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_PIPELINE = [
  { id: 'requested', label: 'Requested', icon: FileText, color: 'text-slate-300', active: 'text-blue-400' },
  { id: 'in_progress', label: 'In Progress', icon: Edit3, color: 'text-slate-300', active: 'text-amber-400' },
  { id: 'ready', label: 'Ready', icon: CheckCircle2, color: 'text-slate-300', active: 'text-emerald-400' },
  { id: 'sent', label: 'Sent', icon: Send, color: 'text-slate-300', active: 'text-cyan-400' },
];

const DEST_TYPES = [
  { value: 'college', label: 'College Application' },
  { value: 'scholarship', label: 'Scholarship' },
  { value: 'employer', label: 'Employment / Internship' },
  { value: 'program', label: 'Special Program' },
  { value: 'other', label: 'Other' },
];

function StatusPipeline({ currentStatus }) {
  const idx = STATUS_PIPELINE.findIndex(s => s.id === currentStatus);
  return (
    <div className="flex items-center gap-1">
      {STATUS_PIPELINE.map((step, i) => {
        const Icon = step.icon;
        const isActive = i <= idx;
        const isCurrent = i === idx;
        return (
          <div key={step.id} className="flex items-center gap-1">
            <div className={`flex items-center gap-1 text-xs font-medium transition-colors ${isActive ? step.active : step.color}`}>
              <Icon className={`w-3.5 h-3.5 ${isCurrent ? 'animate-pulse' : ''}`} />
              <span className="hidden sm:inline">{step.label}</span>
            </div>
            {i < STATUS_PIPELINE.length - 1 && (
              <ChevronRight className={`w-3 h-3 ${isActive && i < idx ? 'text-slate-300' : 'text-slate-300'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function RequestModal({ onClose, onSubmitted }) {
  const { getAuthHeader } = useAuth();
  const [form, setForm] = useState({ destination: '', destination_type: 'college', purpose: '', deadline: '', student_notes: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.destination) { toast.error('Please enter the destination'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/rec-letters/request`, form, getAuthHeader());
      confetti({ particleCount: 80, spread: 70, origin: { y: 0.6 } });
      toast.success(`Request sent to ${r.data.counselor_name}`);
      onSubmitted();
      onClose();
    } catch { toast.error('Could not send request'); }
    finally { setSaving(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-[#0a1729]/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
        className="w-full max-w-lg bg-slate-900 border border-slate-700 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-semibold">Request Recommendation Letter</h3>
            <p className="text-slate-300 text-xs mt-0.5">Your counselor will be notified</p>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-300 hover:text-white" /></button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-300 mb-1 block">Destination *</label>
              <input value={form.destination} onChange={e => set('destination', e.target.value)} required
                placeholder="UNC Chapel Hill" className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500" />
            </div>
            <div>
              <label className="text-xs text-slate-300 mb-1 block">Purpose</label>
              <select value={form.destination_type} onChange={e => set('destination_type', e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white">
                {DEST_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-300 mb-1 block">What should the letter emphasize?</label>
            <textarea value={form.purpose} onChange={e => set('purpose', e.target.value)} rows={2}
              placeholder="e.g. My leadership in the robotics club, my improvement in STEM courses..."
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 resize-none focus:outline-none focus:border-cyan-500" />
          </div>
          <div>
            <label className="text-xs text-slate-300 mb-1 block">Deadline</label>
            <input type="date" value={form.deadline} onChange={e => set('deadline', e.target.value)}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" />
          </div>
          <div>
            <label className="text-xs text-slate-300 mb-1 block">Note for your counselor</label>
            <textarea value={form.student_notes} onChange={e => set('student_notes', e.target.value)} rows={2}
              placeholder="Any specific things you'd like them to know..."
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 resize-none" />
          </div>
          <button type="submit" disabled={saving} data-testid="rec-submit-btn"
            className="w-full py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-semibold disabled:opacity-60">
            {saving ? 'Sending...' : 'Send Request to Counselor'}
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
}

function LetterCard({ letter, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const statusConfig = STATUS_PIPELINE.find(s => s.id === letter.status) || STATUS_PIPELINE[0];
  const Icon = statusConfig.icon;

  return (
    <motion.div className="rounded-xl border border-slate-700/50 bg-slate-800/40 overflow-hidden" data-testid="rec-letter-card">
      <button className="w-full text-left p-4 hover:bg-white/5 transition-colors" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-xs text-slate-300">{DEST_TYPES.find(d => d.value === letter.destination_type)?.label || 'Letter'}</span>
              <span className="text-slate-300">·</span>
              <span className="text-white font-semibold text-sm">{letter.destination}</span>
            </div>
            <StatusPipeline currentStatus={letter.status} />
          </div>
          <div className="text-right flex-shrink-0">
            {letter.deadline && <p className="text-xs text-slate-300">Due {letter.deadline}</p>}
            <p className="text-xs text-slate-300 mt-0.5">{letter.counselor_name}</p>
          </div>
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
            className="border-t border-slate-700/50 px-4 pb-4 pt-3 space-y-3 overflow-hidden">
            {letter.purpose && (
              <div>
                <p className="text-xs text-slate-300 mb-1">What to emphasize</p>
                <p className="text-slate-300 text-sm">{letter.purpose}</p>
              </div>
            )}
            {letter.student_notes && (
              <div>
                <p className="text-xs text-slate-300 mb-1">Your note to counselor</p>
                <p className="text-slate-300 text-sm">{letter.student_notes}</p>
              </div>
            )}
            {letter.status === 'ready' && (
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-emerald-300 text-sm font-medium">Your letter is ready!</p>
                  <p className="text-emerald-400/70 text-xs">Contact your counselor to attach it to your application.</p>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function RecommendationLettersPage() {
  const { getAuthHeader, user } = useAuth();
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequest, setShowRequest] = useState(false);

  const load = () => {
    axios.get(`${API}/rec-letters`, getAuthHeader())
      .then(r => setLetters(r.data.letters || []))
      .catch(() => toast.error('Could not load letters'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const pending = letters.filter(l => ['requested', 'in_progress'].includes(l.status));
  const ready = letters.filter(l => l.status === 'ready');
  const sent = letters.filter(l => l.status === 'sent');

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto space-y-6 pb-16" data-testid="rec-letters-page">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-cyan-400" />
                <span className="text-xs font-semibold uppercase tracking-widest text-cyan-400">Applications</span>
              </div>
              <h1 className="text-4xl font-bold text-white">Recommendation Letters</h1>
              <p className="text-slate-300 text-sm mt-1">
                Request letters from your counselor. Track status from request to sent.
              </p>
            </div>
            <button onClick={() => setShowRequest(true)} data-testid="request-rec-btn"
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-semibold shadow-lg transition-all hover:scale-105">
              <Plus className="w-4 h-4" /> Request Letter
            </button>
          </div>
        </motion.div>

        {/* How it works, the page was silent about what "Request" actually does */}
        <div className="rounded-2xl border border-cyan-400/20 bg-[#0b1b30] p-6" data-testid="rec-letters-how">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-300">How this works</p>
          <div className="mt-4 grid gap-5 md:grid-cols-3">
            {[
              ['1', 'You request', 'Pick who you are asking (counselor or teacher), what it is for, and the deadline. They get the request in their FuturePath inbox, no chasing email.'],
              ['2', 'They write it', 'The writer sees your portfolio, GPA, WBL hours, credentials and goal in one brief, so the letter is specific instead of generic.'],
              ['3', 'You send it', 'When it is marked ready you can attach it to a college, scholarship or program application and the status here changes to Sent.'],
            ].map(([n, t, d]) => (
              <div key={n} className="flex gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-cyan-400/30
                  bg-cyan-400/[0.12] text-xs font-bold text-cyan-200">{n}</span>
                <div>
                  <p className="text-sm font-semibold text-white">{t}</p>
                  <p className="mt-1 text-[12.5px] leading-relaxed text-slate-300">{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Status counts */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'In Progress', count: pending.length, color: 'text-amber-400' },
            { label: 'Ready to Use', count: ready.length, color: 'text-emerald-400' },
            { label: 'Sent', count: sent.length, color: 'text-cyan-400' },
          ].map((s, i) => (
            <div key={i} className="rounded-2xl border border-white/10 bg-[#0b1b30] p-5 text-center">
              <p className={`text-3xl font-bold ${s.color}`}>{s.count}</p>
              <p className="text-slate-300 text-xs mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Letters */}
        {loading ? (
          <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : letters.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-slate-800/60 border border-slate-700 flex items-center justify-center mb-4">
              <FileText className="w-10 h-10 text-slate-300" />
            </div>
            <p className="text-white font-semibold text-lg mb-1">No letters yet</p>
            <p className="text-slate-300 text-sm mb-6">Request your first recommendation letter for college, scholarships, or programs.</p>
            <button onClick={() => setShowRequest(true)} className="px-5 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-medium">
              Request Your First Letter
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {ready.length > 0 && (
              <div>
                <p className="text-emerald-400 text-xs font-semibold uppercase tracking-wide mb-2">Ready</p>
                {ready.map(l => <LetterCard key={l.id} letter={l} onRefresh={load} />)}
              </div>
            )}
            {pending.length > 0 && (
              <div>
                <p className="text-amber-400 text-xs font-semibold uppercase tracking-wide mb-2">In Progress</p>
                {pending.map(l => <LetterCard key={l.id} letter={l} onRefresh={load} />)}
              </div>
            )}
            {sent.length > 0 && (
              <div>
                <p className="text-slate-300 text-xs font-semibold uppercase tracking-wide mb-2">Sent</p>
                {sent.map(l => <LetterCard key={l.id} letter={l} onRefresh={load} />)}
              </div>
            )}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showRequest && <RequestModal onClose={() => setShowRequest(false)} onSubmitted={load} />}
      </AnimatePresence>
    </AppLayout>
  );
}
